/**
 * Saved-view manager governance: grant, revoke, rename, delete, copy, history.
 */
import { labelize } from "./dashboard-html.js";
import { authenticatedFetch } from "./dashboard-auth.js";
import { leadFactorySavedViewScopes, leadFactorySavedViewsUrl } from "./dashboard-urls.js";
import { setSavedViewUrl } from "./dashboard-saved-view-state.js";
import { fetchSavedViewById, fetchSavedViewEvents } from "./dashboard-lead-factory-fetch.js";
import {
  savedViewHistoryHtml,
  savedViewManagerHtml,
  savedViewManagerLoadingHtml
} from "./dashboard-saved-view-manager.js";
import type { DashboardActionHost } from "./dashboard-referenced-deed-actions.js";
import type { LeadFactorySavedView } from "./dashboard-lead-factory-types.js";

type LeadFactorySavedViewScope = (typeof leadFactorySavedViewScopes)[number];

function mustFind<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing dashboard element ${selector}`);
  return element;
}

export type SavedViewActionOptions = { refresh?: boolean };
export type SavedViewAccessGrantPayload = { actor: string; shareRole: string; shareExpiresAt?: string };

export async function grantSavedViewAccess(
  viewId: string,
  payload: SavedViewAccessGrantPayload,
  statusElement: HTMLElement,
  actions: DashboardActionHost,
  options: SavedViewActionOptions = {}
): Promise<boolean> {
  if (!viewId) {
    statusElement.textContent = "Choose a saved view before granting access.";
    return false;
  }
  const actor = payload.actor.trim();
  if (!actor) {
    statusElement.textContent = "Saved-view access grant requires an operator.";
    return false;
  }
  const shareRole = payload.shareRole.trim().toLowerCase() || "viewer";
  const shareExpiresAt = payload.shareExpiresAt?.trim() ?? "";
  statusElement.textContent = "Granting saved-view access...";
  try {
    const response = await authenticatedFetch(`${leadFactorySavedViewsUrl}/${encodeURIComponent(viewId)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ shareWith: [actor], shareRole, shareExpiresAt: shareExpiresAt || undefined })
    });
    const body = (await response.json()) as LeadFactorySavedView & { error?: string; shared_with?: string[] };
    if (!response.ok) throw new Error(body.error ?? `Saved-view access grant failed: ${response.status}`);
    statusElement.textContent = `Granted saved-view access to ${actor}.`;
    if (options.refresh !== false) await Promise.resolve(actions.refresh());
    return true;
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Saved-view access grant failed.";
    return false;
  }
}

export async function revokeSavedViewAccess(viewId: string, actorValue: string, statusElement: HTMLElement, actions: DashboardActionHost, options: SavedViewActionOptions = {}): Promise<boolean> {
  if (!viewId) {
    statusElement.textContent = "Choose a saved view before revoking access.";
    return false;
  }
  const actor = actorValue.trim();
  if (!actor) {
    statusElement.textContent = "Saved-view access revoke requires an operator.";
    return false;
  }
  statusElement.textContent = "Revoking saved-view access...";
  try {
    const response = await authenticatedFetch(`${leadFactorySavedViewsUrl}/${encodeURIComponent(viewId)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ revokeAccessFor: [actor] })
    });
    const body = (await response.json()) as LeadFactorySavedView & { error?: string; revoked_access_for?: string[] };
    if (!response.ok) throw new Error(body.error ?? `Saved-view access revoke failed: ${response.status}`);
    statusElement.textContent = `Revoked saved-view access from ${actor}.`;
    if (options.refresh !== false) await Promise.resolve(actions.refresh());
    return true;
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Saved-view access revoke failed.";
    return false;
  }
}

export async function copySavedViewLink(scope: LeadFactorySavedViewScope, viewId: string, statusElement: HTMLElement): Promise<void> {
  if (!viewId) {
    statusElement.textContent = "Choose a saved view before copying a link.";
    return;
  }
  const url = setSavedViewUrl(scope, viewId);
  try {
    await navigator.clipboard.writeText(url);
    statusElement.textContent = `Copied ${labelize(scope)} saved-view link.`;
  } catch {
    window.prompt("Saved-view link:", url);
    statusElement.textContent = `Prepared ${labelize(scope)} saved-view link.`;
  }
}

export async function showSavedViewHistory(viewId: string, statusElement: HTMLElement): Promise<void> {
  if (!viewId) {
    statusElement.textContent = "Choose a saved view before opening history.";
    return;
  }
  statusElement.textContent = "Loading saved-view history...";
  try {
    const readback = await fetchSavedViewById(viewId);
    const events = readback.events ?? (await fetchSavedViewEvents(viewId)).events;
    statusElement.innerHTML = savedViewHistoryHtml(readback.view, events, readback.access ?? []);
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Saved-view history failed.";
  }
}

export async function showSavedViewManager(viewId: string, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  if (!viewId) {
    statusElement.textContent = "Choose a saved view before opening the manager.";
    return;
  }
  const detail = mustFind<HTMLElement>("#county-detail");
  detail.innerHTML = savedViewManagerLoadingHtml();
  statusElement.textContent = "Loading saved-view manager...";
  try {
    const readback = await fetchSavedViewById(viewId);
    const events = readback.events ?? (await fetchSavedViewEvents(viewId)).events;
    detail.innerHTML = savedViewManagerHtml(readback.view, events, readback.access, readback.access !== undefined);
    bindSavedViewManagerActions(detail, statusElement, readback.view, actions);
    statusElement.textContent = `Opened ${readback.view.name} saved-view manager.`;
  } catch (error) {
    detail.innerHTML = "";
    statusElement.textContent = error instanceof Error ? error.message : "Saved-view manager failed.";
  }
}

export function bindSavedViewManagerActions(detail: HTMLElement, statusElement: HTMLElement, view: LeadFactorySavedView, actions: DashboardActionHost): void {
  detail.querySelector<HTMLButtonElement>("[data-saved-view-manager-close]")?.addEventListener("click", () => {
    detail.innerHTML = "";
  });
  detail.querySelector<HTMLButtonElement>("[data-saved-view-manager-copy]")?.addEventListener("click", () => {
    void copySavedViewLink(view.scope, view.id, statusElement);
  });
  detail.querySelector<HTMLFormElement>("[data-saved-view-manager-rename-form]")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const name = detail.querySelector<HTMLInputElement>("[data-saved-view-manager-name]")?.value ?? "";
    if (await renameSavedView(view.id, name, statusElement, actions, { refresh: false })) {
      await showSavedViewManager(view.id, statusElement, actions);
    }
  });
  detail.querySelector<HTMLFormElement>("[data-saved-view-manager-grant-form]")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const actor = detail.querySelector<HTMLInputElement>("[data-saved-view-manager-grant-actor]")?.value ?? "";
    const shareRole = detail.querySelector<HTMLSelectElement>("[data-saved-view-manager-grant-role]")?.value ?? "viewer";
    const shareExpiresAt = detail.querySelector<HTMLInputElement>("[data-saved-view-manager-grant-expires]")?.value ?? "";
    if (await grantSavedViewAccess(view.id, { actor, shareRole, shareExpiresAt }, statusElement, actions, { refresh: false })) {
      await showSavedViewManager(view.id, statusElement, actions);
    }
  });
  detail.querySelector<HTMLFormElement>("[data-saved-view-manager-revoke-form]")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const actor = detail.querySelector<HTMLSelectElement>("[data-saved-view-manager-revoke-actor]")?.value ?? "";
    if (await revokeSavedViewAccess(view.id, actor, statusElement, actions, { refresh: false })) {
      await showSavedViewManager(view.id, statusElement, actions);
    }
  });
  detail.querySelector<HTMLFormElement>("[data-saved-view-manager-delete-form]")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const confirmed = detail.querySelector<HTMLInputElement>("[data-saved-view-manager-delete-confirm]")?.checked ?? false;
    if (!confirmed) {
      statusElement.textContent = "Confirm saved-view delete before submitting.";
      return;
    }
    if (await deleteSavedView(view.id, statusElement, actions, { refresh: false })) {
      detail.innerHTML = "";
      await Promise.resolve(actions.refresh());
    }
  });
}

export async function renameSavedView(viewId: string, nameValue: string, statusElement: HTMLElement, actions: DashboardActionHost, options: SavedViewActionOptions = {}): Promise<boolean> {
  if (!viewId) {
    statusElement.textContent = "Choose a saved view before renaming it.";
    return false;
  }
  const name = nameValue.trim();
  if (!name) {
    statusElement.textContent = "Saved-view rename requires a name.";
    return false;
  }
  statusElement.textContent = "Renaming saved view...";
  try {
    const response = await authenticatedFetch(`${leadFactorySavedViewsUrl}/${encodeURIComponent(viewId)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name })
    });
    const body = (await response.json()) as LeadFactorySavedView & { error?: string };
    if (!response.ok) throw new Error(body.error ?? `Saved-view rename failed: ${response.status}`);
    statusElement.textContent = `Renamed saved view to ${body.name}.`;
    if (options.refresh !== false) await Promise.resolve(actions.refresh());
    return true;
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Saved-view rename failed.";
    return false;
  }
}

export async function deleteSavedView(viewId: string, statusElement: HTMLElement, actions: DashboardActionHost, options: SavedViewActionOptions = {}): Promise<boolean> {
  if (!viewId) {
    statusElement.textContent = "Choose a saved view before deleting it.";
    return false;
  }
  statusElement.textContent = "Deleting saved view...";
  try {
    const response = await authenticatedFetch(`${leadFactorySavedViewsUrl}/${encodeURIComponent(viewId)}`, { method: "DELETE" });
    const body = (await response.json()) as { deleted?: boolean; error?: string };
    if (!response.ok) throw new Error(body.error ?? `Saved-view delete failed: ${response.status}`);
    statusElement.textContent = "Deleted saved view.";
    if (options.refresh !== false) await Promise.resolve(actions.refresh());
    return true;
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Saved-view delete failed.";
    return false;
  }
}

